import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  FormControl,
  FormHelperText,
  Paper,
  InputAdornment,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Container,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Fade,
  Tooltip,
  FormControlLabel,
  Checkbox,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
  Link
} from "@mui/material";
import { 
  Visibility, 
  VisibilityOff, 
  Email, 
  Lock,
  Person,
  Phone,
  Info,
  CheckCircle,
  Security,
  HelpOutline,
  KeyboardArrowDown,
  ArrowBack,
  ArrowForward
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

// Country codes data with flags - comprehensive list
const countryCodes = [
  { code: "+1", country: "United States", flag: "🇺🇸" },
  { code: "+1", country: "Canada", flag: "🇨🇦" },
  { code: "+44", country: "United Kingdom", flag: "🇬🇧" },
  { code: "+91", country: "India", flag: "🇮🇳" },
  { code: "+61", country: "Australia", flag: "🇦🇺" },
  { code: "+49", country: "Germany", flag: "🇩🇪" },
  { code: "+33", country: "France", flag: "🇫🇷" },
  { code: "+81", country: "Japan", flag: "🇯🇵" },
  { code: "+86", country: "China", flag: "🇨🇳" },
  { code: "+7", country: "Russia", flag: "🇷🇺" },
  { code: "+55", country: "Brazil", flag: "🇧🇷" },
  { code: "+52", country: "Mexico", flag: "🇲🇽" },
  { code: "+34", country: "Spain", flag: "🇪🇸" },
  { code: "+39", country: "Italy", flag: "🇮🇹" },
  { code: "+82", country: "South Korea", flag: "🇰🇷" },
  { code: "+31", country: "Netherlands", flag: "🇳🇱" },
  { code: "+90", country: "Turkey", flag: "🇹🇷" },
  { code: "+966", country: "Saudi Arabia", flag: "🇸🇦" },
  { code: "+27", country: "South Africa", flag: "🇿🇦" },
  { code: "+971", country: "United Arab Emirates", flag: "🇦🇪" },
  { code: "+20", country: "Egypt", flag: "🇪🇬" },
  { code: "+234", country: "Nigeria", flag: "🇳🇬" },
  { code: "+60", country: "Malaysia", flag: "🇲🇾" },
  { code: "+65", country: "Singapore", flag: "🇸🇬" },
  { code: "+64", country: "New Zealand", flag: "🇳🇿" },
  { code: "+46", country: "Sweden", flag: "🇸🇪" },
  { code: "+47", country: "Norway", flag: "🇳🇴" },
  { code: "+45", country: "Denmark", flag: "🇩🇰" },
  { code: "+358", country: "Finland", flag: "🇫🇮" },
  { code: "+48", country: "Poland", flag: "🇵🇱" },
  { code: "+43", country: "Austria", flag: "🇦🇹" },
  { code: "+36", country: "Hungary", flag: "🇭🇺" },
  { code: "+41", country: "Switzerland", flag: "🇨🇭" },
  { code: "+32", country: "Belgium", flag: "🇧🇪" },
  { code: "+351", country: "Portugal", flag: "🇵🇹" },
  { code: "+30", country: "Greece", flag: "🇬🇷" },
  { code: "+353", country: "Ireland", flag: "🇮🇪" },
  { code: "+420", country: "Czech Republic", flag: "🇨🇿" },
  { code: "+380", country: "Ukraine", flag: "🇺🇦" },
  { code: "+40", country: "Romania", flag: "🇷🇴" },
  { code: "+972", country: "Israel", flag: "🇮🇱" },
  { code: "+852", country: "Hong Kong", flag: "🇭🇰" },
  { code: "+886", country: "Taiwan", flag: "🇹🇼" },
  { code: "+66", country: "Thailand", flag: "🇹🇭" },
  { code: "+63", country: "Philippines", flag: "🇵🇭" },
  { code: "+84", country: "Vietnam", flag: "🇻🇳" },
  { code: "+62", country: "Indonesia", flag: "🇮🇩" },
  { code: "+92", country: "Pakistan", flag: "🇵🇰" },
  { code: "+880", country: "Bangladesh", flag: "🇧🇩" },
  { code: "+94", country: "Sri Lanka", flag: "🇱🇰" },
  { code: "+977", country: "Nepal", flag: "🇳🇵" },
  { code: "+95", country: "Myanmar", flag: "🇲🇲" },
  { code: "+998", country: "Uzbekistan", flag: "🇺🇿" },
  { code: "+994", country: "Azerbaijan", flag: "🇦🇿" },
  { code: "+374", country: "Armenia", flag: "🇦🇲" },
  { code: "+995", country: "Georgia", flag: "🇬🇪" },
  { code: "+98", country: "Iran", flag: "🇮🇷" },
  { code: "+964", country: "Iraq", flag: "🇮🇶" },
  { code: "+962", country: "Jordan", flag: "🇯🇴" },
  { code: "+961", country: "Lebanon", flag: "🇱🇧" },
  { code: "+963", country: "Syria", flag: "🇸🇾" },
  { code: "+965", country: "Kuwait", flag: "🇰🇼" },
  { code: "+968", country: "Oman", flag: "🇴🇲" },
  { code: "+973", country: "Bahrain", flag: "🇧🇭" },
  { code: "+974", country: "Qatar", flag: "🇶🇦" },
  { code: "+967", country: "Yemen", flag: "🇾🇪" }
];

// Custom password strength component
const PasswordStrengthMeter = ({ password }) => {
  const calculateStrength = (password) => {
    if (!password) return 0;
    
    let score = 0;
    
    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    
    // Complexity checks
    if (/[A-Z]/.test(password)) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    
    // Normalize score to 0-4 range
    return Math.min(Math.floor(score / 1.5), 4);
  };
  
  const score = calculateStrength(password);
  
  const getColor = (score) => {
    switch (score) {
      case 0: return "#f44336"; // Red
      case 1: return "#ff9800"; // Orange
      case 2: return "#ffeb3b"; // Yellow
      case 3: return "#4caf50"; // Green
      case 4: return "#2e7d32"; // Dark Green
      default: return "#e0e0e0"; // Grey
    }
  };
  
  const getLabel = (score) => {
    switch (score) {
      case 0: return "Very Weak";
      case 1: return "Weak";
      case 2: return "Fair";
      case 3: return "Good";
      case 4: return "Strong";
      default: return "None";
    }
  };
  
  return (
    <Box sx={{ mt: 1, mb: 2 }}>
      <Box sx={{ display: 'flex', mb: 1 }}>
        {[0, 1, 2, 3, 4].map((index) => (
          <Box
            key={index}
            sx={{
              height: 4,
              flex: 1,
              mx: 0.25,
              bgcolor: index <= score ? getColor(score) : '#e0e0e0',
              borderRadius: 1,
              transition: 'background-color 0.3s'
            }}
          />
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
        Password Strength: 
        <Box 
          component="span" 
          sx={{ 
            ml: 1, 
            fontWeight: 600,
            color: score < 2 ? 'error.main' : 
                   score < 3 ? 'warning.main' : 
                   'success.main'
          }}
        >
          {getLabel(score)}
        </Box>
      </Typography>
    </Box>
  );
};

// API URL from environment
const apiUrl = process.env.REACT_APP_API_URL || "/api";
// This is a test key - in production, you would use your own site key
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI";

const SignUp = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { register, error: authError } = useAuth();
  
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    countryCode: "+1",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const [signupError, setSignupError] = useState(""); 
  const [loading, setLoading] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [passwordScore, setPasswordScore] = useState(0);
  const [enableTwoFactor, setEnableTwoFactor] = useState(true);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [countryCodeMenuOpen, setCountryCodeMenuOpen] = useState(false);

  const steps = ['Personal Information', 'Account Details', 'Security Setup', 'Verification'];

  const validateStep1 = () => {
    let newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    let newErrors = {};
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    
    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{6,15}$/.test(formData.phone.replace(/[^0-9]/g, ''))) {
      newErrors.phone = "Phone number is invalid";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    let newErrors = {};
    
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (calculatePasswordScore(formData.password) < 2) {
      newErrors.password = "Password is too weak";
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    
    if (!agreeToTerms) {
      newErrors.terms = "You must agree to the terms and conditions";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep4 = () => {
    let newErrors = {};
    if (!recaptchaValue) {
      newErrors.recaptcha = "Please complete the CAPTCHA verification";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculatePasswordScore = (password) => {
    if (!password) return 0;
    
    let score = 0;
    
    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    
    // Complexity checks
    if (/[A-Z]/.test(password)) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    
    // Normalize score to 0-4 range
    return Math.min(Math.floor(score / 1.5), 4);
  };

  const handleNext = () => {
    let isValid = false;
    
    switch (activeStep) {
      case 0:
        isValid = validateStep1();
        break;
      case 1:
        isValid = validateStep2();
        break;
      case 2:
        isValid = validateStep3();
        break;
      default:
        isValid = true;
    }
    
    if (isValid) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep4()) return;
    
    setLoading(true);
    setSignupError("");
    
    try {
      // Use the register function from AuthContext
      await register({
        ...formData,
        phone: `${formData.countryCode}${formData.phone.replace(/[^0-9]/g, '')}`,
        enableTwoFactor,
        recaptchaToken: recaptchaValue
      });
      
      // Show success message and redirect to sign in
      navigate("/sign-in", { 
        state: { 
          successMessage: "Registration successful! Please sign in with your new account." 
        } 
      });
    } catch (error) {
      setSignupError(error.response?.data?.message || authError || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    if (name === 'password') {
      setPasswordScore(calculatePasswordScore(value));
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
    if (value) {
      setErrors({...errors, recaptcha: undefined});
    }
  };

  const handleTwoFactorChange = (e) => {
    setEnableTwoFactor(e.target.checked);
  };

  const handleTermsChange = (e) => {
    setAgreeToTerms(e.target.checked);
    if (e.target.checked) {
      setErrors((prev) => ({...prev, terms: undefined}));
    }
  };

  const handleCountryCodeChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      countryCode: e.target.value,
    }));
  };

  const getPasswordStrengthLabel = (score) => {
    switch (score) {
      case 0:
        return "Very Weak";
      case 1:
        return "Weak";
      case 2:
        return "Fair";
      case 3:
        return "Good";
      case 4:
        return "Strong";
      default:
        return "None";
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Fade in={activeStep === 0} timeout={500}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleChange}
                  name="firstName"
                  fullWidth
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  value={formData.lastName}
                  onChange={handleChange}
                  name="lastName"
                  fullWidth
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Fade>
        );
      case 1:
        return (
          <Fade in={activeStep === 1} timeout={500}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Email</Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    border: '1px solid rgba(0, 0, 0, 0.23)', 
                    borderRadius: '4px',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                    },
                    ...(errors.email && {
                      borderColor: theme.palette.error.main,
                    })
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      p: 1.5,
                      color: 'action.active'
                    }}>
                      <Email />
                    </Box>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange({
                        target: {
                          name: 'email',
                          value: e.target.value
                        }
                      })}
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        padding: '10px 0',
                        fontSize: '16px',
                        fontFamily: 'inherit',
                        background: 'transparent'
                      }}
                      placeholder="Enter your email"
                    />
                  </Box>
                  {errors.email && (
                    <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.email}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Phone Number</Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    border: '1px solid rgba(0, 0, 0, 0.23)', 
                    borderRadius: '4px',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                    },
                    ...(errors.phone && {
                      borderColor: theme.palette.error.main,
                    })
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      borderRight: '1px solid rgba(0, 0, 0, 0.1)',
                    }}>
                      <Select
                        value={formData.countryCode}
                        onChange={handleCountryCodeChange}
                        displayEmpty
                        variant="standard"
                        disableUnderline
                        IconComponent={KeyboardArrowDown}
                        sx={{ 
                          minWidth: 120,
                          '& .MuiSelect-select': { 
                            py: 1.5, 
                            pl: 1.5,
                            pr: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                          }
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 300,
                            },
                          },
                        }}
                        renderValue={(selected) => {
                          const country = countryCodes.find(c => c.code === selected);
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ mr: 1, fontSize: '1.2rem' }}>{country?.flag}</Box>
                              <Typography variant="body2">{selected}</Typography>
                            </Box>
                          );
                        }}
                      >
                        {countryCodes.map((country) => (
                          <MenuItem key={`${country.code}-${country.country}`} value={country.code}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ mr: 1, fontSize: '1.2rem' }}>{country.flag}</Box>
                              <Typography variant="body2">{country.code}</Typography>
                              <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                                {country.country}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      p: 1.5,
                      color: 'action.active'
                    }}>
                      <Phone />
                    </Box>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange({
                        target: {
                          name: 'phone',
                          value: e.target.value
                        }
                      })}
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        padding: '10px 0',
                        fontSize: '16px',
                        fontFamily: 'inherit',
                        background: 'transparent'
                      }}
                      placeholder="Enter your phone number"
                    />
                  </Box>
                  {errors.phone && (
                    <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.phone}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'info.light', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1
                }}>
                  <Info color="info" sx={{ mt: 0.3 }} />
                  <Typography variant="body2" color="text.secondary">
                    Your email and phone number will be used for account recovery and two-factor authentication. We will never share your information with third parties.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Fade>
        );
      case 2:
        return (
          <Fade in={activeStep === 2} timeout={500}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl 
                  fullWidth 
                  variant="outlined" 
                  error={!!errors.password}
                >
                  <InputLabel htmlFor="password">Password</InputLabel>
                  <OutlinedInput
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    name="password"
                    startAdornment={
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Password"
                  />
                  {errors.password ? (
                    <FormHelperText error>{errors.password}</FormHelperText>
                  ) : (
                    <PasswordStrengthMeter password={formData.password} />
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl 
                  fullWidth 
                  variant="outlined"
                  error={!!errors.confirmPassword}
                >
                  <InputLabel htmlFor="confirm-password">Confirm Password</InputLabel>
                  <OutlinedInput
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    name="confirmPassword"
                    startAdornment={
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowConfirmPassword}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Confirm Password"
                  />
                  {errors.confirmPassword && (
                    <FormHelperText error>{errors.confirmPassword}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Password Requirements:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle 
                        fontSize="small" 
                        color={formData.password.length >= 8 ? "success" : "disabled"} 
                      />
                      <Typography variant="body2" color="text.secondary">
                        At least 8 characters
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle 
                        fontSize="small" 
                        color={/[A-Z]/.test(formData.password) ? "success" : "disabled"} 
                      />
                      <Typography variant="body2" color="text.secondary">
                        At least one uppercase letter
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle 
                        fontSize="small" 
                        color={/[0-9]/.test(formData.password) ? "success" : "disabled"} 
                      />
                      <Typography variant="body2" color="text.secondary">
                        At least one number
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle 
                        fontSize="small" 
                        color={/[^A-Za-z0-9]/.test(formData.password) ? "success" : "disabled"} 
                      />
                      <Typography variant="body2" color="text.secondary">
                        At least one special character
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={enableTwoFactor}
                      onChange={handleTwoFactorChange}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2">Enable Two-Factor Authentication</Typography>
                      <Tooltip title="Two-factor authentication adds an extra layer of security to your account by requiring a verification code in addition to your password.">
                        <IconButton size="small" sx={{ ml: 0.5 }}>
                          <HelpOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreeToTerms}
                      onChange={handleTermsChange}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I agree to the{" "}
                      <Link component={RouterLink} to="/terms" color="primary">
                        Terms of Service
                      </Link>{" "}
                      and{" "}
                      <Link component={RouterLink} to="/privacy" color="primary">
                        Privacy Policy
                      </Link>
                    </Typography>
                  }
                />
                {errors.terms && (
                  <FormHelperText error>{errors.terms}</FormHelperText>
                )}
              </Grid>
            </Grid>
          </Fade>
        );
      case 3:
        return (
          <Fade in={activeStep === 3} timeout={500}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Review Your Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Please verify that all your information is correct before proceeding.
                  </Typography>
                </Box>
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body1">
                        {formData.firstName} {formData.lastName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1">{formData.email}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body1">
                        {formData.countryCode} {formData.phone}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Security
                      </Typography>
                      <Typography variant="body1">
                        {enableTwoFactor ? "Two-factor authentication enabled" : "Standard security"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={handleRecaptchaChange}
                />
              </Grid>
              {errors.recaptcha && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="error" align="center" sx={{ display: 'block' }}>
                    {errors.recaptcha}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Fade>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        minHeight: "calc(100vh - 120px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: { xs: "20px", sm: "40px" },
      }}
    >
      <Container maxWidth="md">
        <Card
          elevation={3}
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
              padding: "30px 20px",
              textAlign: "center",
              color: "white",
            }}
          >
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
              Create Your Account
            </Typography>
            <Typography variant="subtitle1">
              Join our community and start exploring
            </Typography>
          </Box>
          
          <CardContent sx={{ padding: { xs: "20px", sm: "30px" } }}>
            {signupError && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
              >
                {signupError}
              </Alert>
            )}
            
            <Stepper 
              activeStep={activeStep} 
              alternativeLabel={!isMobile}
              orientation={isMobile ? "vertical" : "horizontal"}
              sx={{ mb: 4 }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            
            <form onSubmit={activeStep === steps.length - 1 ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }}>
              {renderStepContent(activeStep)}
              
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={activeStep === 0 || loading}
                  startIcon={<ArrowBack />}
                >
                  Back
                </Button>
                
                {activeStep === steps.length - 1 ? (
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    endIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                  >
                    {loading ? "Creating Account..." : "Complete Registration"}
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowForward />}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </form>
            
            <Box sx={{ mt: 4, textAlign: "center" }}>
              <Typography variant="body2">
                Already have an account?{" "}
                <Link component={RouterLink} to="/sign-in" color="primary">
                  Sign In
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default SignUp;
